/* @jsxImportSource @emotion/react */

import { useState } from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { ChevronDownIcon } from '@slite/icons'

import { useRoutes } from '../../routes'
import { Button } from '../Button/Button'
import type { NavbarWithoutSubmenu, NavbarWithSubmenu } from './data'
import { getNavbarLinksData } from './data'
import './styles.css'

const TOUCH_TARGET_HEIGHT = 48

function hasSubmenu(
  menu: NavbarWithSubmenu | NavbarWithoutSubmenu
): menu is NavbarWithSubmenu {
  return (menu as NavbarWithSubmenu).columns.length > 0
}

// Overlay fills the full viewport below the navbar (64px). The inner
// layout is a flex column: scrollable menu list on top, fixed CTA block
// pinned at the bottom.
const overlayCSS = css`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family-system);
`

// Scrollable list of menu items. Mask-image fades top and bottom so the
// user gets a visual hint that more content is reachable by scrolling.
const scrollAreaCSS = css`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
  -webkit-overflow-scrolling: touch;
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
`

const sectionCSS = css`
  border-top: 1px solid var(--shade-50);
  &:last-of-type {
    border-bottom: 1px solid var(--shade-50);
  }
  padding: 16px 0;
`

const rowCSS = css`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${TOUCH_TARGET_HEIGHT}px;
  padding: 0 25px 0 20px;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  color: var(--shade-05);
  cursor: pointer;
`

const subItemCSS = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: ${TOUCH_TARGET_HEIGHT - 4}px;
  padding: 8px 20px 8px 20px;
  font-size: 18px;
  font-weight: 600;
  color: var(--shade-10);
  text-decoration: none;
`

const subItemIconCSS = css`
  display: inline-flex;
  flex-shrink: 0;
  color: var(--shade-10);
`

const columnTitleCSS = css`
  padding: 16px 20px 8px 20px;
  font-size: 16px;
  font-weight: 400;
  color: var(--shade-30);
`

const ctaContainerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 20px;
  flex-shrink: 0;
`

export const WebsiteNavOverlay = ({
  className,
  background = 'shade-100',
}: {
  className?: string
  background?: string
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const NAVBAR_LINKS_DATA = getNavbarLinksData(intl)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpanded = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <div
      css={overlayCSS}
      className={classNames(
        `background-${background}`,
        'force-light-mode',
        className
      )}
    >
      <div css={scrollAreaCSS}>
        {NAVBAR_LINKS_DATA.map((item, index) => {
          const key = `${item.title}-${index}`

          if (hasSubmenu(item)) {
            const isExpanded = expandedId === item.id
            return (
              <div key={key} css={sectionCSS}>
                <button
                  type="button"
                  css={rowCSS}
                  onClick={() => toggleExpanded(item.id)}
                  aria-expanded={isExpanded}
                >
                  <span style={{ flex: 1 }}>{item.title}</span>
                  <ChevronDownIcon
                    size={16}
                    className={classNames('transition', {
                      'rotate-180': isExpanded,
                    })}
                  />
                </button>
                {isExpanded &&
                  [...item.columns, ...(item.secondaryColumns ?? [])].map(
                    (column, columnIndex) => (
                      <div key={column.title ?? `col-${columnIndex}`}>
                        {column.title && (
                          <div css={columnTitleCSS}>{column.title}</div>
                        )}
                        {column.items.map((subItem, subIndex) => (
                          <a
                            key={`${subItem.url}-${subIndex}`}
                            href={subItem.url}
                            css={subItemCSS}
                          >
                            {subItem.icon && (
                              <span css={subItemIconCSS}>{subItem.icon}</span>
                            )}
                            {subItem.title}
                          </a>
                        ))}
                      </div>
                    )
                  )}
              </div>
            )
          }

          return (
            <div key={key} css={sectionCSS}>
              <a href={item.url} css={rowCSS}>
                {item.title}
              </a>
            </div>
          )
        })}
      </div>

      <div css={ctaContainerCSS}>
        <Button
          variant="primary-brand"
          size="huge"
          href={routes.bookDemo()}
          className="text-align-center"
          style={{
            width: '100%',
          }}
        >
          <FormattedMessage id="navbar.bookDemo" defaultMessage="Book demo" />
        </Button>
        <Button
          size="default"
          variant="ghost"
          href={routes.auth.signin()}
          style={{
            width: '100%',
          }}
        >
          <FormattedMessage id="navbar.signIn" defaultMessage="Sign in" />
        </Button>
      </div>
    </div>
  )
}
